/**
 * Writes that survive a dead lift.
 *
 * The people using this app are in basements, service corridors and car parks.
 * A tap that fails because the phone lost signal for ten seconds does not read
 * as "no signal" — it reads as "this app does not work", and the next time she
 * simply does not tap it.
 *
 * So a write that fails on the network rather than on the server is kept and
 * replayed. Only writes that are safe to repeat: marking a task started, adding
 * an update, clocking in and out. Money and approvals are deliberately not
 * queued — a payment or an approval that lands twenty minutes later, without
 * the person knowing, is worse than one that failed loudly.
 *
 * IndexedDB rather than localStorage because a photo caption plus a queue of
 * writes is easily past the 5MB string limit, and because localStorage is
 * synchronous on the main thread.
 */

const DB_NAME = 'famanage-outbox';
const STORE = 'writes';
const VERSION = 1;

/**
 * Who a queued write belongs to. Stamped at enqueue time so a replay can
 * never land under another person or another household: the replay gate
 * compares userId + householdId and dead-letters a mismatch rather than
 * sending one household's write with another household's token.
 *
 * `userId` / `householdId` come from the access-token JWT claims (sub / hh —
 * apps/api src/auth/service.ts issueTokens). Read, never trusted for
 * authorization — the server re-checks everything; this only decides whether
 * a replay still belongs to the session holding the device.
 */
export interface QueueIdentity {
  userId: string | null;
  householdId: string | null;
}

/** Why a write stopped replaying. It is never silently deleted. */
export type DeadLetterReason = 'identity_mismatch' | 'max_attempts' | 'rejected';

export interface QueuedWrite {
  id: string;
  path: string;
  method: string;
  body: string | null;
  /** What the person was doing, for the banner. */
  label: string;
  queuedAt: string;
  attempts: number;
  /** Who enqueued it. Absent only on rows written before identity binding —
   *  those adopt the current session the first time they are flushed. */
  identity?: QueueIdentity;
  /** One uuid per logical action, generated at enqueue and sent as the
   *  Idempotency-Key header on every replay — the server's dedup for the
   *  "response lost after the write landed" case. */
  idempotencyKey?: string;
  /** Set when the write gave up or was refused — see deadLetters(). */
  deadLetter?: { at: string; reason: DeadLetterReason } | null;
}

/**
 * Paths whose writes may be replayed later.
 *
 * An allowlist, not a denylist. A new endpoint is not silently queueable
 * because somebody forgot to exclude it — the failure mode of a denylist here
 * is a duplicated payment, and nobody wants to find that out in production.
 */
const REPLAYABLE = [
  /^\/v1\/operations\/[^/]+\/transitions\/(in_progress|seen|waiting_proof|waiting_approval)$/,
  /^\/v1\/operations\/[^/]+\/updates$/,
  /^\/v1\/operations\/[^/]+\/mark-seen$/,
  /^\/v1\/operations\/[^/]+\/checklist\/[^/]+$/,
  /^\/v1\/shifts\/clock-(in|out)$/,
  // Same idempotency-key dedup as operation updates (migration 0039) — a
  // basement leaves signal after "the washing machine is leaking" is typed
  // just as often as after any task action.
  /^\/v1\/staff-messages$/,
  // A private note. Qualifies on both counts this list cares about: the
  // create is deduped by the same idempotency key every replay carries
  // (migration 0078), and the PATCH states the note absolutely — title, body,
  // reminder, done — rather than as a delta, so replaying it twice lands the
  // same row. Nobody is paid or approved on this path, so a write that
  // arrives twenty minutes late harms nothing; losing "Maya's passport
  // expires in March" because a lift had no signal harms the whole feature.
  /^\/v1\/notes$/,
  /^\/v1\/notes\/[^/]+$/,
];

export function isReplayable(path: string, method: string): boolean {
  if (!['POST', 'PATCH', 'PUT'].includes(method.toUpperCase())) return false;
  return REPLAYABLE.some((re) => re.test(path));
}

// Same localStorage key api.ts writes. Read directly rather than imported:
// api.ts already imports enqueue/isReplayable from here, and a module cycle
// is how a bundler evaluation-order bug ships.
const TOK_KEY = 'fm.tokens';

/** One uuid per logical action. crypto.randomUUID everywhere modern; the
 *  fallback is collision-safe enough for a per-device queue. */
function newIdempotencyKey(): string {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function hasSession(): boolean {
  try {
    return localStorage.getItem(TOK_KEY) !== null;
  } catch {
    return false;
  }
}

/** The session's identity for outbox binding — JWT claims (user/household).
 *  Nulls when there is no session or the token is not a JWT (tests); a null
 *  field simply cannot prove a mismatch, so it never dead-letters on its own. */
function currentIdentity(): QueueIdentity {
  const none: QueueIdentity = { userId: null, householdId: null };
  try {
    const raw = localStorage.getItem(TOK_KEY);
    if (!raw) return none;
    const tokens = JSON.parse(raw) as { access_token?: string };
    const parts = (tokens.access_token ?? '').split('.');
    if (parts.length !== 3) return none;
    // base64url → base64, re-padded — claims are ASCII ids, no UTF-8 pass.
    const b64 = (parts[1] ?? '').replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))) as {
      sub?: unknown;
      hh?: unknown;
    };
    return {
      userId: typeof payload.sub === 'string' ? payload.sub : null,
      householdId: typeof payload.hh === 'string' ? payload.hh : null,
    };
  } catch {
    return none;
  }
}

/** Proven mismatch only: a field both sides know and that differs. Null
 *  fields (non-JWT token, legacy row) cannot prove anything. */
function identityMismatch(a: QueueIdentity, b: QueueIdentity): boolean {
  if (a.userId && b.userId && a.userId !== b.userId) return true;
  if (a.householdId && b.householdId && a.householdId !== b.householdId) return true;
  return false;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) {
        req.result.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

export async function enqueue(
  write: Omit<QueuedWrite, 'id' | 'queuedAt' | 'attempts' | 'identity' | 'idempotencyKey'>,
) {
  const row: QueuedWrite = {
    ...write,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    queuedAt: new Date().toISOString(),
    attempts: 0,
    identity: currentIdentity(),
    idempotencyKey: newIdempotencyKey(),
    deadLetter: null,
  };
  await withStore('readwrite', (s) => s.add(row));
  notify();
  return row;
}

async function getAll(): Promise<QueuedWrite[]> {
  return withStore<QueuedWrite[]>('readonly', (s) => s.getAll() as IDBRequest<QueuedWrite[]>);
}

/** Live writes, oldest first — dead-lettered rows stay stored but are no
 *  longer "pending"; they surface through deadLetters(). */
export async function pending(): Promise<QueuedWrite[]> {
  try {
    const all = await getAll();
    return all.filter((w) => !w.deadLetter).sort((a, b) => (a.queuedAt < b.queuedAt ? -1 : 1));
  } catch {
    // A browser with IndexedDB blocked (private mode, some embedded webviews)
    // still has to work — it simply has no outbox.
    return [];
  }
}

/** Writes that gave up or were refused — bounded retries exhausted, a server
 *  rejection on the merits, or an identity that no longer holds this device.
 *  Stored and visible; never silently deleted. */
export async function deadLetters(): Promise<QueuedWrite[]> {
  try {
    const all = await getAll();
    return all.filter((w) => w.deadLetter).sort((a, b) => (a.queuedAt < b.queuedAt ? -1 : 1));
  } catch {
    return [];
  }
}

async function drop(id: string) {
  await withStore('readwrite', (s) => s.delete(id));
}

async function put(row: QueuedWrite) {
  await withStore('readwrite', (s) => s.put(row));
}

// Anything that wants to re-render when the queue changes.
const listeners = new Set<() => void>();
export function onQueueChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notify() {
  for (const fn of listeners) fn();
}

/** Give up on a write after this many failed replays. */
const MAX_ATTEMPTS = 8;

// Reconnect fires `online`, visibilitychange and the immediate run within the
// same tick. One flush at a time: a second trigger latches onto the in-flight
// run instead of racing it over the same rows and double-sending.
let inFlight: Promise<{ sent: number; failed: number }> | null = null;

/**
 * Replay everything, oldest first.
 *
 * Order matters: "started" before "finished" on the same task is the difference
 * between a valid transition and a 422. One at a time, and a failure stops the
 * run rather than racing ahead — the next reconnect will pick it up.
 *
 * A write is dead-lettered — kept and visible, never silently deleted — when:
 *   - the server rejects it on its merits (4xx that is not 408/429): the state
 *     has moved on, and replaying it every time the phone finds signal is a
 *     queue that never empties;
 *   - its replay budget runs out (MAX_ATTEMPTS);
 *   - it belongs to a different user or household than the session holding
 *     the device. It is NEVER replayed under another identity.
 */
export function flush(
  send: (w: QueuedWrite) => Promise<{ ok: boolean; status: number }>,
): Promise<{ sent: number; failed: number }> {
  inFlight ??= flushInner(send).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function flushInner(
  send: (w: QueuedWrite) => Promise<{ ok: boolean; status: number }>,
): Promise<{ sent: number; failed: number }> {
  let sent = 0;
  let failed = 0;
  // Signed out: never replay — there is no token to send with, and a 401
  // would dead-letter writes that are simply waiting for a sign-in.
  if (!hasSession()) return { sent, failed };
  const me = currentIdentity();
  const deadLetter = async (w: QueuedWrite, reason: DeadLetterReason) => {
    await put({ ...w, deadLetter: { at: new Date().toISOString(), reason } });
    failed++;
  };
  for (const queued of await pending()) {
    let w = queued;
    // The identity gate runs FIRST, before any field repair — a row that can
    // prove it belongs to another user or household is dead-lettered, never
    // sent under this session, however incomplete its fields are.
    if (w.identity && identityMismatch(w.identity, me)) {
      await deadLetter(w, 'identity_mismatch');
      continue;
    }
    if (!w.identity || !w.idempotencyKey) {
      // Legacy row: adopt the current identity and give it the key every
      // replay now carries (see QueuedWrite.identity).
      w = {
        ...w,
        identity: w.identity ?? me,
        idempotencyKey: w.idempotencyKey ?? newIdempotencyKey(),
      };
      await put(w);
    }
    if (w.attempts >= MAX_ATTEMPTS) {
      await deadLetter(w, 'max_attempts');
      continue;
    }
    try {
      const res = await send(w);
      if (res.ok) {
        await drop(w.id);
        sent++;
      } else if (res.status === 401) {
        // NOT a rejection of the write. Access tokens live ~15 minutes, so a
        // clock-in queued on the way into a basement and replayed an hour
        // later 401s purely because the token aged out — and dead-lettering
        // it turned "it will send when you have signal" into "it gave up",
        // on the exact promise this queue exists to keep. The sender refreshes
        // and retries once; anything still 401 after that waits for the next
        // flush, by which time the app's own refresh may have succeeded.
        await put({ ...w, attempts: w.attempts + 1 });
        break;
      } else if (res.status >= 400 && res.status < 500 && ![408, 429].includes(res.status)) {
        await deadLetter(w, 'rejected');
      } else {
        await put({ ...w, attempts: w.attempts + 1 });
        break;
      }
    } catch {
      // Still offline. Leave the queue exactly as it is.
      await put({ ...w, attempts: w.attempts + 1 });
      break;
    }
  }
  if (sent || failed) notify();
  return { sent, failed };
}

/** Wire the automatic replay: on reconnect, and once on load. */
export function startOutbox(send: (w: QueuedWrite) => Promise<{ ok: boolean; status: number }>) {
  const run = () => {
    void flush(send);
  };
  window.addEventListener('online', run);
  // Coming back to the app is as good a signal as `online` — a phone that
  // never fired the event still knows it has signal when you look at it.
  const onVisible = () => {
    if (document.visibilityState === 'visible' && navigator.onLine) run();
  };
  document.addEventListener('visibilitychange', onVisible);
  if (navigator.onLine) run();
  return () => {
    window.removeEventListener('online', run);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
